// Parses a recipient input string into a list of email candidate tokens.
//
// Handles two common shapes:
//   1. Plain delimited list: "a@x.com, b@y.com" or "a@x.com b@y.com"
//   2. RFC 5322 mailbox format: '"Display Name" <email@domain.com>'
//      Gmail, Outlook, and Slack place this on the clipboard when copying
//      contacts. The display name is discarded so display-name fragments
//      don't trigger spurious "invalid email" errors during validation.
//
// Returned tokens are not validated here — the caller runs its own
// `isValidEmail` check so typos like "notanemail" still surface an error
// when the user types them directly (no brackets present).
export function parseEmailInput(input: string): string[] {
  const candidates: string[] = [];

  // Extract <email> segments first. We deliberately strip the surrounding
  // display name (e.g. '"Alice"' or 'Alice') because it would otherwise be
  // split into tokens that fail email validation and confuse the user.
  const stripped = input.replace(/<([^<>]+)>/g, (_, addr: string) => {
    const trimmed = addr.trim();
    if (trimmed) candidates.push(trimmed);
    return " ";
  });

  const hadBrackets = candidates.length > 0;

  for (const token of stripped.split(/[\s,;]+/)) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    // When brackets were present we ignore anything outside them that doesn't
    // look like an email (display-name remnants). When no brackets were
    // present we keep every non-empty token so the caller's validator can
    // flag obvious typos as before.
    if (hadBrackets && !trimmed.includes("@")) continue;
    candidates.push(trimmed);
  }

  return candidates;
}
